/**
 * Per-user shopping & planner preferences.
 *
 * Persisted in the `user_shopping_prefs` table:
 *  - `department_order` — user-customised department order. Empty array =
 *    use the app default (`DEPARTMENT_ORDER` from `./departments`).
 *  - `correction_history` — short rolling log of recent classification
 *    corrections, used to power "Move X to Y like last time?" prompts.
 *    We keep at most ~50 entries; older entries are dropped on update.
 *  - `default_meal_slots` — user's preferred default visible meal slots
 *    for new meal plans. Each plan can override its own.
 *
 * Cached per-session keyed by lowercased email.
 */

import { supabase } from "@/integrations/supabase/client";
import { DEPARTMENT_ORDER, type Department } from "./departments";
import {
  DEFAULT_VISIBLE_MEALS,
  sanitiseVisibleMeals,
  type MealSlot,
} from "./planner";

const HISTORY_MAX = 50;

export type CorrectionEntry = {
  /** Canonical ingredient key. */
  key: string;
  /** Department the user moved it from. */
  from: Department;
  /** Department the user moved it to. */
  to: Department;
  /** ISO timestamp. */
  at: string;
};

export type UserShoppingPrefs = {
  departmentOrder: Department[];
  correctionHistory: CorrectionEntry[];
  defaultMealSlots: MealSlot[];
};

const cache = new Map<string, UserShoppingPrefs>();
const cacheKey = (email: string) => email.trim().toLowerCase();

const isDept = (v: unknown): v is Department =>
  typeof v === "string" && (DEPARTMENT_ORDER as readonly string[]).includes(v);

const sanitiseOrder = (raw: unknown): Department[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<Department>();
  const out: Department[] = [];
  for (const v of raw) {
    if (isDept(v) && !seen.has(v)) {
      out.push(v);
      seen.add(v);
    }
  }
  // Append any departments the user hasn't ordered (e.g. a new dept added
  // by a future release) so we never drop one entirely.
  if (out.length > 0) {
    for (const d of DEPARTMENT_ORDER) {
      if (!seen.has(d)) out.push(d);
    }
  }
  return out;
};

const sanitiseHistory = (raw: unknown): CorrectionEntry[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (e: unknown): e is CorrectionEntry =>
        !!e &&
        typeof e === "object" &&
        typeof (e as CorrectionEntry).key === "string" &&
        isDept((e as CorrectionEntry).from) &&
        isDept((e as CorrectionEntry).to) &&
        typeof (e as CorrectionEntry).at === "string",
    )
    .slice(-HISTORY_MAX);
};

export const getUserShoppingPrefs = async (
  email: string,
): Promise<UserShoppingPrefs> => {
  const k = cacheKey(email);
  const cached = cache.get(k);
  if (cached) return cached;

  const { data, error } = await supabase
    .from("user_shopping_prefs")
    .select("department_order, correction_history, default_meal_slots")
    .eq("email", k)
    .maybeSingle();

  if (error) {
    console.error("[userShoppingPrefs] load failed", error);
  }

  const prefs: UserShoppingPrefs = {
    departmentOrder: sanitiseOrder(data?.department_order),
    correctionHistory: sanitiseHistory(data?.correction_history),
    defaultMealSlots: data
      ? sanitiseVisibleMeals(data.default_meal_slots)
      : [...DEFAULT_VISIBLE_MEALS],
  };
  cache.set(k, prefs);
  return prefs;
};

const persist = async (
  email: string,
  patch: Partial<{
    department_order: Department[];
    correction_history: CorrectionEntry[];
    default_meal_slots: MealSlot[];
  }>,
) => {
  const k = cacheKey(email);
  const current = cache.get(k) ?? {
    departmentOrder: [],
    correctionHistory: [],
    defaultMealSlots: [...DEFAULT_VISIBLE_MEALS],
  };
  const next: UserShoppingPrefs = {
    departmentOrder:
      patch.department_order !== undefined
        ? sanitiseOrder(patch.department_order)
        : current.departmentOrder,
    correctionHistory:
      patch.correction_history !== undefined
        ? sanitiseHistory(patch.correction_history)
        : current.correctionHistory,
    defaultMealSlots:
      patch.default_meal_slots !== undefined
        ? sanitiseVisibleMeals(patch.default_meal_slots)
        : current.defaultMealSlots,
  };
  cache.set(k, next);

  const { error } = await supabase
    .from("user_shopping_prefs")
    .upsert(
      {
        email: k,
        department_order: next.departmentOrder,
        correction_history: next.correctionHistory,
        default_meal_slots: next.defaultMealSlots,
      },
      { onConflict: "email" },
    );
  if (error) {
    console.error("[userShoppingPrefs] save failed", error);
    throw error;
  }
};

export const saveDepartmentOrder = (email: string, order: Department[]) =>
  persist(email, { department_order: order });

export const resetDepartmentOrder = (email: string) =>
  persist(email, { department_order: [] });

export const saveDefaultMealSlots = (email: string, slots: MealSlot[]) =>
  persist(email, { default_meal_slots: slots });

/** Append a correction entry, capped to `HISTORY_MAX` most recent. */
export const recordCorrection = async (
  email: string,
  entry: Omit<CorrectionEntry, "at">,
) => {
  const k = cacheKey(email);
  const current = cache.get(k) ?? (await getUserShoppingPrefs(email));
  const next = [
    ...current.correctionHistory,
    { ...entry, at: new Date().toISOString() },
  ].slice(-HISTORY_MAX);
  await persist(email, { correction_history: next });
};

/**
 * Look at recent corrections to see whether the user has consistently moved
 * `key` to a particular department. If so, return that department — caller
 * can offer a one-tap "Move it like last time?" suggestion.
 *
 * Triggers only when the same (key → dept) pair has happened ≥ 2 times AND
 * is the most recent direction for that key.
 */
export const suggestionForKey = (
  prefs: UserShoppingPrefs,
  key: string,
  currentDept: Department,
): Department | null => {
  if (!key) return null;
  const matches = prefs.correctionHistory.filter((e) => e.key === key);
  if (matches.length < 2) return null;
  const latest = matches[matches.length - 1];
  if (latest.to === currentDept) return null;
  // Require last 2 to agree on the same target.
  const last = matches.slice(-2);
  if (!last.every((e) => e.to === latest.to)) return null;
  return latest.to;
};

/** Resolve the effective department order — user's saved order or default. */
export const effectiveDepartmentOrder = (prefs: UserShoppingPrefs): Department[] => {
  if (prefs.departmentOrder.length > 0) return prefs.departmentOrder;
  return [...DEPARTMENT_ORDER];
};

export const clearUserShoppingPrefsCache = (email?: string) => {
  if (email) cache.delete(cacheKey(email));
  else cache.clear();
};
